import { ColDef, ISetFilterParams } from 'ag-grid-community'
import { ChevronDown } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'
import type { DropdownSelectItem } from '@/components/lemmala-design-system/dropdown/dropdown-select.types'
import {
  TABLE_FILTER_BAR_LABELS,
  TABLE_LABELS,
} from '@/components/lemmala-design-system/table/labels'
import {
  collapseSelectedValues,
  nestFilterValues,
} from '@/components/lemmala-design-system/table/utils/nested-filter-values'
import {
  clearButtonVariants,
  filterTriggerVariants,
} from '@/components/lemmala-design-system/table/variants'
import { personFilterLabels } from '@/components/shared/person-filter/labels'
import { usePersonFilterDropdownItems } from '@/components/shared/person-filter/use-person-filter-dropdown-items'

import { AgFilterClearButton } from './ag-filter-clear-button'
import { AgFilterRemoveButton } from './ag-filter-remove-button'

type SetFilterParams = ISetFilterParams<Record<string, unknown>, string> & {
  personFilter?: boolean
  singleSelect?: boolean
  /** The values listed under each value. Checking a value checks the ones under it. */
  childValues?: Record<string, string[]>
  /** An icon shown after a value's label. */
  getIcon?: (value: string) => ReactNode
}

export interface AgFilterProps<TRowData = Record<string, unknown>> {
  columnDef: ColDef<TRowData>
  selectedValues: string[]
  hideSuffix?: boolean
  defaultOpen?: boolean
  onChange: (field: string, values: string[]) => void
  onClear: (field: string) => void
  onRemove?: () => void
  // Important to allow filter popovers to catch interactions
  inModal?: boolean
}

const withSuffixIcons = (
  items: DropdownSelectItem[],
  getIcon: ((value: string) => ReactNode) | undefined,
): DropdownSelectItem[] =>
  getIcon
    ? items.map((item) => ({
        ...item,
        suffixIcon: getIcon(item.id),
        children: item.children && withSuffixIcons(item.children, getIcon),
      }))
    : items

const uniqueStrings = (values: unknown): string[] => {
  const raw = Array.isArray(values) ? values.filter((value): value is string => value != null) : []
  return Array.from(new Set(raw))
}

export const AgFilter = <TRowData = Record<string, unknown>,>({
  columnDef,
  selectedValues,
  hideSuffix = false,
  defaultOpen = false,
  onChange,
  onClear,
  onRemove,
  inModal = false,
}: AgFilterProps<TRowData>) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const filterParams = columnDef.filterParams as SetFilterParams
  const isPersonFilter = filterParams?.personFilter ?? false
  const {
    dropdownItems: personDropdownItems,
    optionIds: personOptionIds,
    isLoading: isPersonFilterLoading,
  } = usePersonFilterDropdownItems(isPersonFilter)

  const safeSelectedValues = useMemo(() => selectedValues || [], [selectedValues])

  const values = useMemo(() => filterParams?.values ?? [], [filterParams?.values])
  const childValues = useMemo(() => filterParams?.childValues ?? {}, [filterParams?.childValues])
  const singleSelect = filterParams?.singleSelect ?? false

  // Restored selections stay listed so they can be cleared if this table's rows omit them.
  const options = useMemo(() => {
    let base: string[]
    if (isPersonFilter) {
      base = personOptionIds.length > 0 ? personOptionIds : uniqueStrings(values)
    } else {
      base = uniqueStrings(values)
    }
    const missing = safeSelectedValues.filter((value) => !base.includes(value))
    return missing.length > 0 ? [...base, ...missing] : base
  }, [isPersonFilter, personOptionIds, values, safeSelectedValues])

  const dropdownItems = useMemo<DropdownSelectItem[]>(() => {
    if (isPersonFilter && personDropdownItems.length > 0) {
      const existingIds = new Set(personDropdownItems.map((item) => item.id))
      const missingItems = options
        .filter((id) => !existingIds.has(id))
        .map((id) => ({ id, label: id }))
      return missingItems.length > 0
        ? [...personDropdownItems, ...missingItems]
        : personDropdownItems
    }
    return withSuffixIcons(nestFilterValues(options, childValues), filterParams?.getIcon)
  }, [isPersonFilter, options, personDropdownItems, childValues, filterParams?.getIcon])

  const selectedCount = safeSelectedValues.length
  const hasAnySelection = selectedCount > 0

  const fieldKey = (columnDef.colId as string) || (columnDef.field as string)

  const namedSelectedValues = useMemo(
    () => collapseSelectedValues(safeSelectedValues, dropdownItems),
    [safeSelectedValues, dropdownItems],
  )

  const getSelectedDisplayText = useMemo(() => {
    const namedCount = namedSelectedValues.length
    if (namedCount === 1) {
      return namedSelectedValues[0]
    }
    if (namedCount === 2) {
      return `${namedSelectedValues[0]}, ${namedSelectedValues[1]}`
    }
    return `${namedSelectedValues[0]}+${namedCount - 1}`
  }, [namedSelectedValues])

  const handleMultiValueChange = useMemo(
    () => (selectedIds: string[]) => {
      onChange(fieldKey, selectedIds)
    },
    [onChange, fieldKey],
  )

  const handleItemSelect = useMemo(
    () => (itemId: string) => {
      const isCurrentlySelected = safeSelectedValues.includes(itemId)
      if (isCurrentlySelected) {
        onChange(fieldKey, [])
      } else {
        onChange(fieldKey, [itemId])
      }
    },
    [onChange, fieldKey, safeSelectedValues],
  )

  return (
    <div className='flex items-center gap-px'>
      <div className='flex items-start justify-start gap-px'>
        <DropdownSelect
          open={isOpen}
          isModal={inModal}
          onOpenChange={setIsOpen}
          items={dropdownItems}
          multi={!singleSelect}
          selectedItems={safeSelectedValues}
          onItemSelect={singleSelect ? handleItemSelect : undefined}
          onMultiValueChange={singleSelect ? undefined : handleMultiValueChange}
          search={isPersonFilter || dropdownItems.length >= 10}
          searchPlaceholder={
            isPersonFilter
              ? personFilterLabels.searchPlaceholder
              : TABLE_FILTER_BAR_LABELS.filterPlaceholder(columnDef.headerName)
          }
          showTooltips
          trigger={
            <Button
              size='default'
              variant='ghost'
              className={filterTriggerVariants({
                state: hasAnySelection ? 'selected' : 'unselected',
                attached: !!onRemove,
              })}
            >
              <span className='leading-5'>
                {hasAnySelection ? (
                  <>
                    <span className='text-body-default'>{columnDef.headerName}: </span>
                    <span className='text-label-default'>{getSelectedDisplayText}</span>
                  </>
                ) : (
                  columnDef.headerName
                )}
              </span>
              {!hasAnySelection && !hideSuffix && (
                <ChevronDown className='size-4 text-main-foreground-tertiary' />
              )}
            </Button>
          }
          footerContent={
            hasAnySelection ? (
              <Button
                size='sm'
                variant='link'
                className={clearButtonVariants({ active: hasAnySelection })}
                onClick={() => {
                  onClear(fieldKey)
                }}
              >
                {TABLE_FILTER_BAR_LABELS.clearAll}
              </Button>
            ) : isPersonFilter && isPersonFilterLoading ? (
              <div className='px-2 py-1 text-main-foreground-tertiary text-body-small'>
                {TABLE_LABELS.loading}
              </div>
            ) : undefined
          }
        />

        {hasAnySelection && !onRemove && (
          <AgFilterClearButton
            onClear={() => {
              onClear(fieldKey)
            }}
          />
        )}
      </div>

      {onRemove && <AgFilterRemoveButton hasSelection={hasAnySelection} onRemove={onRemove} />}
    </div>
  )
}
